import {
  ArrowRightLeft,
  Bell,
  Building2,
  ClipboardCheck,
  Factory,
  FileClock,
  FileOutput,
  FileType,
  FlaskConical,
  FolderOpen,
  Gauge,
  Key,
  LineChart,
  Settings,
  ShieldCheck,
  ShoppingCart,
  TrendingDown,
  Users,
  Zap,
} from 'lucide-react';
import { FEATURE_CHECKS } from '@/features/dms/constants/permissions';
import type { PermissionCheck } from '@/types/permissions';
import { COMMON_CHECKS } from '@/types/permissions';

export interface NavBadge {
  useCount: () => { count: number };
  variant?: 'primary' | 'warning';
}

export interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  requiredPermissions?: PermissionCheck[];
  badge?: NavBadge;
}

export interface NavSection {
  title?: string;
  links: NavItem[];
}

// Badge hooks are lazily imported by dms app config to avoid cross-app contamination
export function createDmsNavItems(badges?: {
  reviewCount?: NavBadge;
  pdfStatusCount?: NavBadge;
  openCapaCount?: NavBadge;
  openEcrCount?: NavBadge;
  calibrationAlertCount?: NavBadge;
}): NavSection[] {
  return [
    // General Section
    {
      links: [
        {
          href: '/',
          label: 'Documents',
          icon: FolderOpen,
          end: true,
        },
        {
          href: '/reviews',
          label: 'Reviews',
          icon: ClipboardCheck,
          badge: badges?.reviewCount,
        },
      ],
    },
    // Purchasing Section
    {
      title: 'Purchasing',
      links: [
        {
          href: '/suppliers',
          label: 'Approved Supplier List',
          icon: Factory,
          requiredPermissions: [FEATURE_CHECKS.PURCHASING],
        },
        {
          href: '/purchase-orders/create',
          label: 'Purchase Orders',
          icon: ShoppingCart,
          requiredPermissions: [FEATURE_CHECKS.PURCHASING],
        },
      ],
    },
    // Quality Section
    {
      title: 'Quality',
      links: [
        {
          href: '/capas',
          label: 'CAPAs',
          icon: ShieldCheck,
          requiredPermissions: [FEATURE_CHECKS.CAPAS],
          badge: badges?.openCapaCount,
        },
        {
          href: '/ecrs',
          label: 'ECRs',
          icon: ArrowRightLeft,
          requiredPermissions: [FEATURE_CHECKS.ECRS],
          badge: badges?.openEcrCount,
        },
        {
          href: '/calibration',
          label: 'Calibration',
          icon: Gauge,
          requiredPermissions: [FEATURE_CHECKS.CALIBRATION],
          badge: badges?.calibrationAlertCount,
        },
      ],
    },
    // Testing Section
    {
      title: 'Testing',
      links: [
        {
          href: '/testing/runs',
          label: 'Test Runs',
          icon: FlaskConical,
          requiredPermissions: [FEATURE_CHECKS.TESTING],
        },
        {
          href: '/testing/failure-analysis',
          label: 'Failure Analysis',
          icon: TrendingDown,
          requiredPermissions: [FEATURE_CHECKS.TESTING],
        },
        {
          href: '/testing/performance-analysis',
          label: 'Performance',
          icon: LineChart,
          requiredPermissions: [FEATURE_CHECKS.TESTING],
        },
      ],
    },
    // Admin Section
    {
      title: 'Admin',
      links: [
        {
          href: '/admin/users',
          label: 'User Management',
          icon: Users,
          requiredPermissions: [COMMON_CHECKS.ADMIN],
        },
        {
          href: '/admin/document-types',
          label: 'Document Types',
          icon: FileType,
          requiredPermissions: [FEATURE_CHECKS.DOCUMENT_TYPES],
        },
        {
          href: '/admin/pdf-generation',
          label: 'PDF Generation',
          icon: FileOutput,
          requiredPermissions: [FEATURE_CHECKS.PDF_GENERATION],
          badge: badges?.pdfStatusCount,
        },
        {
          href: '/admin/purchasing-settings',
          label: 'Purchasing Settings',
          icon: Settings,
          requiredPermissions: [FEATURE_CHECKS.PURCHASING_SETTINGS],
        },
        {
          href: '/admin/capa-settings',
          label: 'CAPA Settings',
          icon: ShieldCheck,
          requiredPermissions: [FEATURE_CHECKS.CAPA_SETTINGS],
        },
        {
          href: '/admin/audit',
          label: 'Audit Logs',
          icon: FileClock,
          requiredPermissions: [COMMON_CHECKS.ADMIN],
        },
        {
          href: '/admin/ate/tokens',
          label: 'ATE Tokens',
          icon: Key,
          requiredPermissions: [FEATURE_CHECKS.ATE_TOKENS],
        },
      ],
    },
  ];
}

// Badge hooks are lazily imported by platform app config to avoid cross-app contamination
export function createPlatformNavItems(badges?: { notificationCount?: NavBadge }): NavSection[] {
  return [
    // General Section
    {
      links: [
        {
          href: '/organizations',
          label: 'Organizations',
          icon: Building2,
        },
        {
          href: '/notifications',
          label: 'Notifications',
          icon: Bell,
          badge: badges?.notificationCount,
        },
      ],
    },
    // Admin Section
    {
      title: 'Admin',
      links: [
        {
          href: '/admin/users',
          label: 'User Management',
          icon: Users,
          requiredPermissions: [COMMON_CHECKS.ADMIN],
        },
        {
          href: '/admin/audit',
          label: 'Audit Logs',
          icon: FileClock,
          requiredPermissions: [COMMON_CHECKS.ADMIN],
        },
        {
          href: '/admin/event-generator',
          label: 'Event Generator',
          icon: Zap,
          requiredPermissions: [COMMON_CHECKS.ADMIN],
        },
      ],
    },
  ];
}
